import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { CalendarEventModel, CreateCalendarEventDto } from '@/types/calendar';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as getCalendarEventQueryKey } from './useGetCalendarEvent';

const METHOD = 'PUT';
const ENDPOINT = 'calendar-events/:id';

interface UpdateCalendarEventParams {
  id: number;
  data: Partial<CreateCalendarEventDto>;
}

interface UseUpdateCalendarEventOptions {
  onSuccess?: (data: CalendarEventModel) => void;
  onError?: (error: unknown) => void;
}

export const useUpdateCalendarEvent = (options?: UseUpdateCalendarEventOptions) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<CalendarEventModel, unknown, UpdateCalendarEventParams>({
    mutationFn: async ({ id, data }: UpdateCalendarEventParams) => {
      try {
        return await handleApiCall<CalendarEventModel, Partial<CreateCalendarEventDto>>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {
            id: id.toString(),
          }),
          data // Properly typed now, no need for 'as any'
        );
      } catch (err) {
        enqueueSnackbar('Failed to update calendar event.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: data => {
      // Invalidate specific query for this event
      queryClient.invalidateQueries({ queryKey: getCalendarEventQueryKey(data.id) });

      // Invalidate all events queries
      queryClient.invalidateQueries({
        queryKey: [['GET', 'calendar-events']],
        exact: false,
      });

      if (options?.onSuccess) {
        options.onSuccess(data);
      }

      enqueueSnackbar('Calendar event updated successfully.', { variant: 'success' });
    },
    onError: error => {
      if (options?.onError) {
        options.onError(error);
      }
    },
  });
};
